import { INVENTORIES_CATEGORY } from "../../../constants/shop";
import { InventoryCategory } from "@onmo/graphql/graphql";
import { getListInventoriesGQL, getUserInventoriesGQL } from "../../../graphql";
import { useCallback, useMemo } from "react";
import { Inventories } from "../../../models/inventories";
import { debounce, filter, groupBy, orderBy } from "lodash-es";
import { useQuery } from "@apollo/client";
import {
  Inventory,
  InventoryBundleData,
  InventoryStatus,
} from "@onmo/graphql/graphql";

export const inputListBundle = {
  input: {
    filters: {
      restrictToBundles: true,
      restrictToFree: false,
      categories: INVENTORIES_CATEGORY,
    },
  },
};

export const inputListInventories = {
  input: {
    filters: { restrictToItems: true, categories: INVENTORIES_CATEGORY },
  },
};

export const inputListVouchers = {
  input: {
    filters: {
      status: InventoryStatus.Available,
      restrictToBundles: true,
      categories: [InventoryCategory.Epay],
    },
  },
};

const LIMIT = 50;

//fetch user inventories
export const useFetchUserInventories = () => {
  const { data, fetchMore } = useQuery(getUserInventoriesGQL, {
    variables: {
      input: {
        page: { limit: LIMIT, offset: 0 },
        filters: {
          status: [InventoryStatus.Available, InventoryStatus.Restricted],
        },
      },
    },
  });
  const inventories = data?.me?.inventories?.items;

  const loadMore = useCallback(() => {
    if (!inventories) return [];
    fetchMore({
      variables: {
        input: {
          page: { limit: LIMIT, offset: inventories.length },
        },
      },
    });
  }, [fetchMore, inventories]);
  const loadMoreAvatar = useMemo(() => debounce(loadMore, 300), [loadMore]);

  return { userInventories: inventories, loadMoreAvatar: loadMoreAvatar };
};

//fetch list inventories
export const useFetchListInventories = () => {
  const { data } = useQuery(getListInventoriesGQL, {
    variables: inputListInventories,
  });
  return data?.inventories?.items;
};

//fetch list bundle
export const useFetchListBundles = () => {
  const { data } = useQuery(getListInventoriesGQL, {
    variables: inputListBundle,
  });
  return data?.inventories?.items;
};

// list avatar owned
export const useAvatarsOwned = () => {
  const { userInventories } = useFetchUserInventories();
  if (!userInventories) return;
  const avatarsOwned = userInventories?.filter(
    (i: Inventory) => !i.isBundle && i.isPurchased === true,
  );

  return avatarsOwned;
};

// list bundle owned
export const useBundlesOwned = () => {
  const listBundle = useFetchListBundles();
  if (!listBundle) return;

  const ownedBundles = listBundle.filter(
    (i: Inventory) =>
      i.isBundle && i.isPurchased && i.status === InventoryStatus.Available,
  );
  return ownedBundles;
};

export const useAvatarGroupByCategoryOwned = () => {
  const { userInventories, loadMoreAvatar } = useFetchUserInventories();
  if (!userInventories) return;

  const ownedAvatarGroupByCategory = groupBy(
    userInventories?.filter((i: Inventory) => !i.isBundle),
    "category",
  );

  return { ownedAvatar: ownedAvatarGroupByCategory, loadMoreAvatar };
};

//list bundle not purchase
export const useBundlesNotPurchase = () => {
  const listBundle = useFetchListBundles();
  if (!listBundle) return;

  const ownedItemsGroupByCategory = orderBy(
    filter(listBundle, {
      isBundle: true,
      isPurchased: false,
      status: InventoryStatus.Available,
      category: InventoryCategory.Avatar,
    }),
    ["updatedAt"],
    ["desc"],
  );
  return ownedItemsGroupByCategory;
};

//list avatar not purchase
export const useAvatarsNoPurchase = () => {
  const listInventories = useFetchListInventories();
  if (!listInventories) return;

  const avatarNotPurchase = orderBy(listInventories, ["updatedAt"], ["desc"]);
  const listAvatar = () => {
    const avatars = filter(avatarNotPurchase, (inventory) => {
      return (
        [InventoryStatus.Available, InventoryStatus.Restricted].includes(
          inventory?.status,
        ) && !inventory?.isPurchased
      );
    });
    const newAvatars = Inventories.sortAvatars(avatars);
    return newAvatars;
  };
  return listAvatar();
};

export const useListAvatarByBundle = (bundleId: string) => {
  const { data } = useQuery(getListInventoriesGQL, {
    variables: {
      input: {
        filters: { bundleInventoryId: bundleId },
      },
    },
  });
  const inventories = data?.inventories?.items;

  const listAvatarByBundle = useMemo(() => {
    const inventoryFiltered = inventories?.filter((item: Inventory) =>
      [InventoryStatus.Available, InventoryStatus.Restricted].includes(
        item.status,
      ),
    );
    return Inventories.sortAvatars(inventoryFiltered);
  }, [inventories]);
  return listAvatarByBundle;
};

//get bundle by avatar id
export const useCurrentBundleByAvatar = (avatarId: string) => {
  const listBundle = useFetchListBundles();
  if (!listBundle) return;

  const bundle = listBundle?.find((inventory: Inventory) =>
    (inventory.data as InventoryBundleData).items.includes(avatarId),
  );
  return bundle;
};

//get bundle by bundle id
export const useBundleByBundleId = (bundleId: string) => {
  const listBundle = useFetchListBundles();
  if (!listBundle) return;

  const bundle = listBundle?.find(
    (bundle: Inventory) => bundle.id === bundleId,
  );
  return bundle;
};

// get list of free Avatars
export const useFetchFreeAvatars = () => {
  const { data, loading, error } = useQuery(getListInventoriesGQL, {
    variables: {
      input: {
        page: {
          limit: 9,
          offset: 0,
        },
        filters: {
          restrictToFree: true,
          restrictToItems: true,
          categories: ["AVATAR"],
          status: [InventoryStatus.Available, InventoryStatus.Restricted],
        },
      },
    },
  });

  return {
    freeAvatars: data?.inventories?.items || [],
    total: data?.inventories?.total || 0,
    loading,
    error,
  };
};

// fetch epay vouchers bundle
export const useFetchVouchersBundle = (): Inventory[] | undefined => {
  const { data } = useQuery(getListInventoriesGQL, {
    variables: inputListVouchers,
  });
  return data?.inventories?.items;
};

export const useEpayVoucherByBundleId = (bundleId?: string): Inventory | undefined => {
  const listBundle = useFetchVouchersBundle();
  if (!listBundle) return;

  const bundle = listBundle?.find(
    (bundle: Inventory) => bundle.id === bundleId,
  );
  return bundle;
};

// get list of items in epay bundle
export const useListItemsByVoucherBundle = (bundleId?: string): { itemList: Inventory[] | undefined; loading: boolean } => {
  const { data, loading } = useQuery(getListInventoriesGQL, {
    variables: {
      input: {
        filters: { bundleInventoryId: bundleId },
      },
    },
    skip: !bundleId,
  });
  const inventories = data?.inventories?.items;

  const listItemsByBundle = useMemo(() => {
    const inventoryFiltered = inventories?.filter(
      (item: Inventory) =>
        [InventoryStatus.Available].includes(item.status) &&
        item.category === InventoryCategory.Epay,
    );
    return inventoryFiltered;
  }, [inventories]);
  return { itemList: listItemsByBundle, loading };
}
  
export const useItemsByItemId = (bundleId?: string, itemId?: string): { item: Inventory | undefined; loading: boolean; } => {
  const { itemList, loading } = useListItemsByVoucherBundle(bundleId);
  const item = itemList?.find((listItem: Inventory) => listItem.id === itemId);
  return { item, loading };
}